<template>
    <config-section
        :title="$t('auth.two_factor_authentication')"
        :description="$t('auth.two_factor_auth_description')"
    >
        <div>
            <h2 class="text-body mb-8">{{ enabled ? $t('auth.two_factor_enabled') : $t('auth.two_factor_disabled')}}</h2>
            <p class="text-primary-80">{{ $t('auth.two_factor_auth_info') }}</p>
        </div>

        <div v-if="!enabled && qrCode" class="pt-24">
            <div class="mb-16 font-semibold">
                {{ $t('auth.two_factor_qr_info') }}
            </div>
            <div v-html="qrCode"></div>
            <div class="mt-16" autocomplete="off">
                <div class="mb-8 font-semibold capitalize">{{ $t('auth.auth_code') }}</div>
                <simple-input
                        v-model="form.verificationCode"
                        :error="form.errors.verificationCode"
                        name="verificationCode"
                        ref="verificationCode"
                        type="text"
                        autocomplete="off"
                        ></simple-input>
            </div>
        </div>

        <div v-if="enabled && recoveryCodes" class="pt-24">
            <div class="mb-16 font-semibold">
                {{ $t('auth.recovery_codes_info') }}
            </div>
            <copy-to-clipboard
                :copy-value="recoveryCodes.join('\n')"
            >
                <ul class="font-mono grow">
                    <li v-for="code in recoveryCodes" :key="code">
                        {{ code }}
                    </li>
                </ul>
            </copy-to-clipboard>
        </div>

        <template #buttons>
            <div v-if="!active && !enabled">
                <simple-button
                        display="secondary"
                        :loading="activatingTwoFactorAuth"
                        look="primary"
                        @click="activate"
                        >{{ $t('auth.enable_two_factor_auth') }}</simple-button>
            </div>
            <div v-if="active && !enabled">
                <simple-button
                        display="secondary"
                        look="primary"
                        @click="submitVerificationCode"
                >
                    {{ $t('two_factor_config.verify')}}
                </simple-button>
            </div>
            <div v-if="enabled" class="flex space-x-8">
                <simple-button
                    display="secondary"
                    :loading="regeneratingRecoveryCodes"
                    look="primary"
                    v-if="recoveryCodes"
                    @click="regenerateRecoveryCodes"
                >{{ $t('auth.regenerate_recovery_codes') }}</simple-button>
                <simple-button
                    display="secondary"
                    :loading="retrievingRecoveryCodes"
                    look="primary"
                    v-else
                    @click="retrieveRecoveryCodes"
                >{{ $t('auth.show_recovery_codes') }}</simple-button>
                <simple-button
                    v-if="allowDisabling"
                    @click="disable"
                    display="danger"
                    :loading="disablingTwoFactorAuth"
                >{{ $t('auth.disable_two_factor_auth') }}</simple-button>
            </div>
        </template>
    </config-section>
</template>

<script>
import ConfigurationSection from './ConfigurationSection.vue';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useAuthenticationStore} from '../state/modules/authentication';
import User from '../models/User';
import SimpleButton from './ui/SimpleButton.vue';
import SvgIcon from './ui/SvgIcon.vue';
import Input from './forms/Input.vue';
import Form from '../etc/forms/Form';
import CopyToClipboard from "./ui/CopyToClipboard.vue";

export default {
    components: {
        CopyToClipboard,
        'config-section': ConfigurationSection,
        'simple-input': Input,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
    },
    data: function () {
        return {
            qrCode: null,
            recoveryCodes: null,
            active: false,
            enabled: false,
            allowDisabling: false,
            activatingTwoFactorAuth: false,
            disablingTwoFactorAuth: false,
            retrievingRecoveryCodes: false,
            regeneratingRecoveryCodes: false,
            form: new Form([
                { name: 'verificationCode', value: '', rules: []},
            ]),
        }
    },
    computed: {
        ...mapState(useAuthenticationStore, ['currentUser']),
    },
    methods: {
        activate() {
            this.activatingTwoFactorAuth = true;
            axios.post(route('app.activate-2fa'))
            .then((response) => {
                this.active = true
                this.verificationCode = ''
                this.qrCode = response.data.qrCode;
                this.recoveryCodes = response.data.recoveryCodes;
            })
            .finally(() => {
                this.activatingTwoFactorAuth = false;
            });
        },
        disable() {
            this.disablingTwoFactorAuth = true;
            axios.post(route('app.disable-2fa'))
            .then(async (response) => {
                if (!response.data.allow) return;
                await this.toggleUserEnabled(false);
                this.active = false
                this.enabled = false
                this.qrCode = null;
                this.recoveryCodes = null;
            })
            .finally(() => {
                this.disablingTwoFactorAuth = false;
            });
        },
        submitVerificationCode() {
            this.form.submit((data) => {
                return axios.post(route('app.submit-2fa-verification-code'), data)
            })
            .then(async () => {
                await this.toggleUserEnabled(true)
                this.enabled = true
            })
            .catch((error) => {
                this.$refs.verificationCode.focus()
            })
            .finally(() => {
                this.form.verificationCode = ''
            })
        },
        retrieveRecoveryCodes() {
            this.retrievingRecoveryCodes = true;
            axios.get(route('app.recovery-codes'))
            .then(async (response) => {
                this.recoveryCodes = response.data;
            })
            .finally(() => {
                this.retrievingRecoveryCodes = false;
            });
        },
        regenerateRecoveryCodes() {
            this.regeneratingRecoveryCodes = true;
            axios.post(route('app.recovery-codes'))
            .then(async (response) => {
                this.recoveryCodes = response.data;
            })
            .finally(() => {
                this.regeneratingRecoveryCodes = false;
            });
        },
        toggleUserEnabled(isEnabled) {
            return useRepo(User)
                .whereId(this.currentUser.id)
                .update({'two_factor_enabled': isEnabled})
        },
        fetchAllowDisabling() {
            axios.get(route('app.allow-2fa-disable'))
                .then((response) => {
                    this.allowDisabling = response.data.allow
                })
        },
    },
    mounted() {
        this.enabled = this.currentUser.two_factor_enabled
        this.fetchAllowDisabling()
    }
}
</script>
